import React, { useState } from 'react';
import { unblockPrompt, type Block } from '../domain/safety';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import ConfirmDialog from '../components/ConfirmDialog';
import { ActionError, EmptyState, LoadingState, StateForError } from '../components/PageStates';
import UserAvatar from '../components/UserAvatar';
import { fullName } from '../utils/people';
import { Button } from '@/components/ui/button';
import { ShieldOff } from 'lucide-react';

/**
 * `/settings/blocked` — the people I've blocked, newest first (017 FR-023).
 * Nobody can see who blocked them. Unblocking ends that one block and
 * brings nothing back (FR-024).
 */
export default function BlockedPeoplePage() {
  const { safety } = useRepositories();
  const blocks = useLoad<Block[]>(() => safety.blocks(), 'blocks');
  const [unblocking, setUnblocking] = useState<Block | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const unblock = async (block: Block) => {
    setBusy(true);
    setError(null);
    try {
      await safety.endBlock(block.id);
      blocks.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode;
  if (blocks.status === 'loading') body = <LoadingState label="Loading blocked people" />;
  else if (blocks.status === 'error')
    body = <StateForError error={blocks.error} onRetry={blocks.reload} />;
  else if (blocks.data.length === 0)
    body = (
      <EmptyState
        icon={<ShieldOff className="size-8" />}
        title="No blocked people"
        description="To block someone, tap their name wherever you see it."
      />
    );
  else
    body = (
      <ul aria-label="People you blocked" className="space-y-3">
        {blocks.data.map((block) => {
          const name = block.person ? fullName(block.person) : 'Deleted account';
          return (
            <li key={block.id} className="flex items-center gap-4 rounded-3xl bg-card p-5">
              {block.person ? (
                <UserAvatar person={block.person} className="size-12" textClassName="text-base" />
              ) : (
                <div className="size-12 shrink-0 rounded-full bg-muted" aria-hidden="true" />
              )}
              <p className="min-w-0 flex-1 font-semibold text-foreground">{name}</p>
              <Button
                variant="secondary"
                size="sm"
                className="rounded-full"
                aria-label={block.person ? `Unblock ${name}` : 'Unblock this account'}
                disabled={busy}
                onClick={() => setUnblocking(block)}
              >
                Unblock
              </Button>
            </li>
          );
        })}
      </ul>
    );

  const prompt = unblocking && unblockPrompt(unblocking.person);

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Blocked people</h1>
        <p className="mt-2 text-muted-foreground">
          You and they can't message or see each other's listings. Only you see this list.
        </p>
      </div>
      {error !== null && <ActionError error={error} />}
      {body}
      {unblocking && prompt && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setUnblocking(null)}
          title={prompt.title}
          description={<p>{prompt.body}</p>}
          confirmLabel="Unblock"
          onConfirm={() => unblock(unblocking)}
        />
      )}
    </div>
  );
}
